import { Building2, ExternalLink, Plus, Trash2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import SafeImage from "../components/SafeImage";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { deleteProject } from "../lib/datasource";
import { eur0 } from "../lib/format";
import { totalRooms, totalRoomsFromMix } from "../lib/rooms";
import { lastSavedLabel } from "../lib/utils";
import { Deal } from "../types/deal";
import { useAuth } from "@/auth/SupabaseAuthProvider";
import { DealStub, listProjectsSlim } from "@/lib/datasource/supabase";

export default function UnderwritingHome() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [deals, setDeals] = useState<DealStub[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const loadDeals = useCallback(async (userId: string) => {
    try {
      setLoading(true);
      const data = await listProjectsSlim(userId);
      setDeals(data);
    } catch (e) {
      console.error("[UnderwritingHome] Failed to load:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadDeals(user.id);
    }
  }, [user?.id, loadDeals]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteProject(confirmDelete);
      setDeals((prev) => prev.filter((d) => d.id !== confirmDelete));
      setConfirmDelete(null);
    } catch (e) {
      console.error("Delete failed:", e);
    }
  };

  const renderDealCard = (deal: Deal) => {
    const roomsCountFromTypes = totalRooms(deal.roomTypes);
    const roomsCountFromMix = totalRoomsFromMix(deal.roomMix);

    return (
      <Card
        key={deal.id}
        className="border-slate-200 hover:shadow-md transition-shadow overflow-hidden"
      >
        <div className="h-32 bg-slate-100">
          <SafeImage
            src={deal.photoUrl}
            fallbackText={deal.name}
            className="w-full h-full object-cover"
          />
        </div>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <CardTitle className="text-lg truncate mr-2">{deal.name}</CardTitle>
            <span className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">
              {deal.id.slice(0, 8)}
            </span>
          </div>
          <p className="text-sm text-slate-500 truncate">{deal.location}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
            {/* <span>
              Rooms (types): <strong>{roomsCountFromTypes}</strong>
            </span> */}
            <span>
              Rooms (mix): <strong>{roomsCountFromMix}</strong>
            </span>
            <span>
              Price: <strong>{eur0(deal.purchasePrice)}</strong>
            </span>
            <span className="col-span-2">
              Saved: {lastSavedLabel(deal.updatedAt)}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate(`/underwriting/${deal.id}`)}
              className="flex-1 bg-primary-blue"
            >
              <ExternalLink className="w-4 h-4 mr-2" /> Open
            </Button>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(deal.id)}
              className="text-red-500 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <header className="flex justify-between items-center mb-8">
        <p className="text-slate-500">
          Manage your hotel underwriting projects
        </p>
        <Button
          onClick={() => navigate("/underwriting/create")}
          className="bg-[#1d4ed8]"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Deal
        </Button>
      </header>

      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[300px] w-full rounded-xl" />
          ))}
        </div>
      ) : deals.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {deals.map(renderDealCard)}
        </div>
      )}

      {confirmDelete && (
        <DeleteModal
          onClose={() => setConfirmDelete(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

const EmptyState = () => (
  <Card className="border-dashed border-2 p-12 text-center">
    <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
    <h3 className="text-lg font-medium">No deals yet</h3>
    <p className="text-slate-500 mb-6">Create your first deal to get started</p>
    <Link to="/underwriting/create">
      <Button className="bg-primary-blue">Create Deal</Button>
    </Link>
  </Card>
);

const DeleteModal = ({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-sm">
      <CardHeader>
        <CardTitle>Delete Deal?</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-slate-500 mb-6">
          This action is permanent and cannot be undone.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
          >
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
);
